import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { MessageSquare, Send, Loader2, AlertCircle, X, User, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { API } from "../../App";
import { useNavigate } from "react-router-dom";

const MAX_CHARS = 1600; // 10 segments hard cap
const SEGMENT_SIZE = 160; // 7-bit GSM
const SEGMENT_WARN = 3;

const formatPhoneDisplay = (raw) => {
  if (!raw) return "";
  const digits = (raw + "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
};

/**
 * Compact "Send Quick SMS" composer used on the Call History page.
 * Reuses POST /api/sms/send — same Twilio pipeline the Messages page uses.
 */
const QuickSmsModal = ({
  open,
  onClose,
  toNumber,          // required — recipient phone number
  fromNumber,        // required — user's business number (Twilio-owned)
  customerName,      // optional — display label above the number
  onSent,            // optional callback — fires on successful send
}) => {
  const navigate = useNavigate();
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);       // { code, message, upgrade?, invalid? }
  const [optOutStatus, setOptOutStatus] = useState(null); // { opted_out, opted_out_at, reason }
  const [checkingOptOut, setCheckingOptOut] = useState(false);
  // "From" number picker (only rendered when the account has 2+ business numbers)
  const [availableFromNumbers, setAvailableFromNumbers] = useState([]);
  const [selectedFromNumber, setSelectedFromNumber] = useState(fromNumber || "");

  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Reset when opening + fetch from-numbers & opt-out status
  useEffect(() => {
    if (!open) return;
    setBody("");
    setError(null);
    setOptOutStatus(null);
    setSelectedFromNumber(fromNumber || "");

    // Fetch business phone numbers (for the "From" picker when there are 2+)
    axios
      .get(`${API}/settings/phone-numbers`, { headers })
      .then((r) => {
        const raw = Array.isArray(r.data) ? r.data : (r.data?.phone_numbers || []);
        // Any active number the account owns can send SMS — business lines,
        // job-source tracking lines, dispatcher lines. Only call-masking
        // proxy numbers are excluded (they're reserved for relay routing).
        const usable = raw.filter(
          (p) => p.is_active !== false && p.phone_number && p.purpose !== "call_masking"
        );
        setAvailableFromNumbers(usable);
        // If the call's business number isn't in the usable list (shouldn't happen), fall back to first usable
        if (fromNumber && !usable.some((p) => p.phone_number === fromNumber) && usable[0]) {
          setSelectedFromNumber(usable[0].phone_number);
        }
      })
      .catch(() => setAvailableFromNumbers([]));

    if (!toNumber) return;
    setCheckingOptOut(true);
    axios
      .get(`${API}/sms/optout-status`, { headers, params: { phone_number: toNumber } })
      .then((r) => setOptOutStatus(r.data))
      .catch(() => setOptOutStatus(null))
      .finally(() => setCheckingOptOut(false));
  }, [open, toNumber, fromNumber, headers]);

  // Segment math
  const charCount = body.length;
  const segments = charCount === 0 ? 0 : Math.ceil(charCount / SEGMENT_SIZE);
  const isOverMax = charCount > MAX_CHARS;
  const isMultiSegment = segments > 1;
  const isHighSegment = segments >= SEGMENT_WARN;

  const isOptedOut = !!optOutStatus?.opted_out;
  const canSend = !sending && body.trim().length > 0 && !isOverMax && !isOptedOut;

  const handleSend = async () => {
    if (!canSend) return;
    // Warn before sending 3+ segments
    if (isHighSegment) {
      const ok = window.confirm(
        `This message will be sent as ${segments} SMS segments. Most carriers charge per segment. Send anyway?`
      );
      if (!ok) return;
    }
    setSending(true);
    setError(null);
    try {
      const resp = await axios.post(
        `${API}/sms/send`,
        { from_number: selectedFromNumber || fromNumber, to_number: toNumber, body: body.trim() },
        { headers }
      );
      toast.success(`SMS sent to ${formatPhoneDisplay(toNumber)}`);
      if (typeof onSent === "function") onSent(resp.data);
      onClose();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Parse structured detail (opt-out, insufficient funds)
      if (detail && typeof detail === "object") {
        if (detail.code === "INSUFFICIENT_FUNDS" || status === 402) {
          setError({
            code: "INSUFFICIENT_FUNDS",
            message: detail.message || "SMS limit reached. Upgrade your plan or add funds to continue.",
            upgrade: true,
          });
        } else if (detail.code === "RECIPIENT_OPTED_OUT") {
          setError({ code: "RECIPIENT_OPTED_OUT", message: detail.message });
        } else {
          setError({ message: detail.message || JSON.stringify(detail) });
        }
      } else if (typeof detail === "string") {
        setError({ message: detail });
      } else {
        setError({ message: e?.message || "Failed to send SMS. Please try again." });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !sending && onClose()}>
      <DialogContent className="bg-background border-border max-w-md" data-testid="quick-sms-modal">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-500" /> Send Quick SMS
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* From picker — only rendered when the user has 2+ business numbers */}
          {availableFromNumbers.length > 1 && (
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">From</Label>
              <Select
                value={selectedFromNumber}
                onValueChange={setSelectedFromNumber}
                disabled={sending}
              >
                <SelectTrigger className="mt-1" data-testid="quick-sms-from-select">
                  <SelectValue placeholder="Select a business number" />
                </SelectTrigger>
                <SelectContent>
                  {availableFromNumbers.map((p) => (
                    <SelectItem key={p.phone_number} value={p.phone_number}>
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="font-mono">{formatPhoneDisplay(p.phone_number)}</span>
                        {p.label && (
                          <span className="text-xs text-muted-foreground">· {p.label}</span>
                        )}
                        {p.assignment_type === "job_source" && (
                          <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">Job Source</span>
                        )}
                        {p.assignment_type === "dispatcher" && (
                          <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">Dispatcher</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* To */}
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">To</Label>
            <div className="mt-1 rounded-lg border border-border bg-muted/30 p-3">
              {customerName ? (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="font-medium text-foreground" data-testid="quick-sms-customer">
                    {customerName}
                  </span>
                </div>
              ) : null}
              <div className="flex items-center gap-2 mt-0.5">
                <Phone className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-sm text-muted-foreground font-mono" data-testid="quick-sms-to-number">
                  {formatPhoneDisplay(toNumber)}
                </span>
              </div>
            </div>
          </div>

          {/* Opt-out banner — blocks send entirely */}
          {isOptedOut && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 flex items-start gap-2" data-testid="quick-sms-optout-banner">
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-700 dark:text-red-400">Recipient has opted out</p>
                <p className="text-xs text-red-600/80 dark:text-red-300/80 mt-0.5">
                  This number replied STOP to your business. Sending is blocked. They must text START to re-subscribe.
                </p>
              </div>
            </div>
          )}

          {/* Message */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Message</Label>
              <div className="flex items-center gap-2 text-xs">
                <span
                  className={isOverMax ? "text-red-500 font-semibold" : "text-muted-foreground"}
                  data-testid="quick-sms-char-count"
                >
                  {charCount}/{MAX_CHARS}
                </span>
                {isMultiSegment && (
                  <Badge
                    className={isHighSegment
                      ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[10px] h-5"
                      : "bg-blue-500/15 text-blue-600 dark:text-blue-400 text-[10px] h-5"}
                    data-testid="quick-sms-segments"
                  >
                    {segments} segments
                  </Badge>
                )}
              </div>
            </div>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, MAX_CHARS))}
              placeholder="Type your message…"
              rows={5}
              maxLength={MAX_CHARS}
              disabled={sending || isOptedOut}
              className="resize-none"
              data-testid="quick-sms-body"
            />
            {isHighSegment && !isOverMax && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                Heads-up: this will send as {segments} SMS segments. Carriers usually charge per segment.
              </p>
            )}
          </div>

          {/* Inline error (send failure) */}
          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm" data-testid="quick-sms-error">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-700 dark:text-red-400">{error.message}</p>
                  {error.upgrade && (
                    <Button
                      size="sm"
                      className="mt-2 bg-red-600 text-white hover:bg-red-600/90"
                      onClick={() => { onClose(); navigate("/dashboard/settings?tab=my-plan"); }}
                      data-testid="quick-sms-upgrade-btn"
                    >
                      Upgrade plan
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {checkingOptOut && (
            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Checking opt-out status…
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={sending}
            data-testid="quick-sms-cancel-btn"
          >
            <X className="w-4 h-4 mr-1.5" /> Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!canSend}
            className="bg-blue-600 text-white hover:bg-blue-600/90"
            data-testid="quick-sms-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickSmsModal;
